// src/services/oldschoolDungeonEscapeService.ts
import { logger } from '../utils/logger';

import { supabase } from './supabase';
import { SaveData } from '../components/games/dungeon/dungeonTypes';

export interface OldschoolDungeonEscapeSave {
  id: string;
  user_id: string;
  save_data: SaveData;
  created_at: string;
  updated_at: string;
}

/**
 * Save game to cloud (Supabase)
 */
export async function saveGameToCloud(saveData: SaveData): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      logger.error('No user logged in - cannot save to cloud');
      return false;
    }

    // Upsert the save data (insert or update)
    const { error } = await supabase
      .from('dungeon_escape_saves')
      .upsert({
        user_id: user.id,
        save_data: saveData,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'user_id'
      });

    if (error) {
      logger.error('Error saving to cloud:', error);
      return false;
    }

    return true;
  } catch (error) {
    logger.error('Error in saveGameToCloud:', error);
    return false;
  }
}

/**
 * Load game from cloud (Supabase)
 */
export async function loadGameFromCloud(): Promise<SaveData | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      logger.error('No user logged in - cannot load from cloud');
      return null;
    }

    const { data, error } = await supabase
      .from('dungeon_escape_saves')
      .select('save_data')
      .eq('user_id', user.id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No save found - not an error, just return null
        return null;
      }
      logger.error('Error loading from cloud:', error);
      return null;
    }

    return data?.save_data ?? null;
  } catch (error) {
    logger.error('Error in loadGameFromCloud:', error);
    return null;
  }
}

/**
 * Check if user has a cloud save
 */
export async function hasCloudSave(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return false;
    }

    const { data, error } = await supabase
      .from('dungeon_escape_saves')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (error && error.code !== 'PGRST116') {
      logger.error('Error checking cloud save:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    logger.error('Error in hasCloudSave:', error);
    return false;
  }
}

/**
 * Delete cloud save
 */
export async function deleteCloudSave(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      logger.error('No user logged in - cannot delete cloud save');
      return false;
    }

    const { error } = await supabase
      .from('dungeon_escape_saves')
      .delete()
      .eq('user_id', user.id);

    if (error) {
      logger.error('Error deleting cloud save:', error);
      return false;
    }

    return true;
  } catch (error) {
    logger.error('Error in deleteCloudSave:', error);
    return false;
  }
}
